/**
 * Shimmerballs — keeps the dispatch promise line truthful across variant changes.
 *
 * Mirrors the pattern in product-inventory.js: on a variant/product select the
 * theme hands us the freshly rendered section HTML, and we morph our own subtree
 * from it. Nothing is computed client-side — the state always comes from Liquid,
 * so the line can never drift from what the server would say.
 */
import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

class SbDispatch extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  #section = null;

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        // Match by tag name so the line and the image pill each pick up their own.
        const incoming = html.querySelector(this.tagName.toLowerCase());
        if (!incoming) return;

        // Carry the state class across too — it drives the dot colour.
        this.className = incoming.className;
        morph(this, incoming, { childrenOnly: true });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sb-dispatch] Event promise rejected:', error);
      });
  };
}

/**
 * The pill is rendered next to the promise line (inside the product-information section)
 * and relocated into <media-gallery> here, so the theme's 37KB media-gallery snippet never
 * needs editing. If no gallery is present the pill simply removes itself rather than
 * appearing in the wrong place.
 */
class SbDispatchBadge extends SbDispatch {
  connectedCallback() {
    super.connectedCallback();
    this.#relocate();
  }

  #relocate() {
    if (this.dataset.mounted === 'true') return;
    const gallery = document.querySelector('media-gallery');
    if (!gallery) {
      this.remove();
      return;
    }
    this.dataset.mounted = 'true';
    gallery.appendChild(this);
  }
}

if (!customElements.get('sb-dispatch')) {
  customElements.define('sb-dispatch', SbDispatch);
}

if (!customElements.get('sb-dispatch-badge')) {
  customElements.define('sb-dispatch-badge', SbDispatchBadge);
}
